import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WorkoutTimelineCard } from '../components/workout/WorkoutTimelineCard';
import { PremiumCard, PremiumEmpty, PremiumPage, SectionHeader } from '../components/premium';
import { NavIcon } from '../components/NavIcons';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { addDaysToDateKey, localDateKey, localDateKeyFromIso } from '../lib/dateKey';
import { isCardioActivityType, normalizeActivityType } from '../lib/workoutActivity';
import {
  findPersonalRecords,
  formatVolumeKg,
  monthlyVolumeKg,
  muscleGroupVolume,
  sortWorkoutsNewest,
  weeklyVolumeKg,
  workoutDatesSet,
  workoutsThisMonth,
  workoutsThisWeek,
  workoutSetCount,
} from '../lib/workoutStats';
import { workoutDayStreakFromIso } from '../lib/workoutStreak';
import { kgToLbs } from '../lib/units';
import type { Workout } from '../types';

type HistoryTab = 'timeline' | 'analytics' | 'calendar';
type FilterId = 'all' | 'week' | 'month' | 'strength' | 'cardio';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'strength', label: 'Strength' },
  { id: 'cardio', label: 'Cardio' },
];

function WorkoutMonthCalendar({ workouts, monthOffset }: { workouts: Workout[]; monthOffset: number }) {
  const dates = workoutDatesSet(workouts);
  const view = useMemo(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(day);
    return { year, month, label, cells };
  }, [monthOffset]);

  return (
    <PremiumCard className="!p-4">
      <div className="mb-3 text-sm font-bold text-[var(--text-primary)]">{view.label}</div>
      <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {view.cells.map((day, i) => {
          if (day == null) return <div key={`e-${i}`} />;
          const key = `${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const hasWorkout = dates.has(key);
          const isToday = key === localDateKey();
          return (
            <div
              key={key}
              className={`flex aspect-square items-center justify-center rounded-lg text-xs font-medium ${
                isToday ? 'ring-1 ring-sky-400/50' : ''
              } ${hasWorkout ? 'bg-sky-500/25 font-bold text-sky-200' : 'text-[var(--text-muted)]'}`}
            >
              {day}
            </div>
          );
        })}
      </div>
    </PremiumCard>
  );
}

function MuscleHeatmap({ workouts, imperial }: { workouts: Workout[]; imperial: boolean }) {
  const rows = muscleGroupVolume(workouts);
  const maxVol = Math.max(...rows.map((r) => r.volumeKg), 1);

  if (!rows.length) {
    return <p className="text-xs text-[var(--text-muted)]">Log strength work to see muscle balance.</p>;
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const pct = Math.round((row.volumeKg / maxVol) * 100);
        const volLabel = row.volumeKg > 0 ? formatVolumeKg(row.volumeKg, imperial) : `${row.sets} sets`;
        return (
          <div key={row.key}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-[var(--text-secondary)]">{row.label}</span>
              <span className="tabular-nums text-[var(--text-muted)]">{volLabel}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-600 to-sky-400 transition-all"
                style={{ width: `${Math.max(pct, row.sets > 0 && row.volumeKg <= 0 ? 8 : 0)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function WorkoutHistoryPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const imperial = user?.preferredUnitSystem === 'imperial';

  const [list, setList] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<HistoryTab>('timeline');
  const [filter, setFilter] = useState<FilterId>('all');
  const [query, setQuery] = useState('');
  const [menuWorkoutId, setMenuWorkoutId] = useState<string | null>(null);
  const [monthOffset, setMonthOffset] = useState(0);

  const refresh = useCallback(() => {
    setLoading(true);
    api<Workout[]>('/api/workouts')
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    let rows = sortWorkoutsNewest(list);
    if (filter === 'week') rows = workoutsThisWeek(rows);
    else if (filter === 'month') rows = workoutsThisMonth(rows);
    else if (filter === 'strength') rows = rows.filter((w) => !isCardioActivityType(normalizeActivityType(w.activityType)));
    else if (filter === 'cardio') rows = rows.filter((w) => isCardioActivityType(normalizeActivityType(w.activityType)));

    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (w) =>
          w.name.toLowerCase().includes(q) ||
          w.exercises.some((e) => e.name.toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [list, filter, query]);

  const streak = workoutDayStreakFromIso(list.map((w) => w.startedAt));
  const weekVol = weeklyVolumeKg(list, Date.now() - 7 * 86400000);
  const monthVol = monthlyVolumeKg(list);
  const weekSessions = workoutsThisWeek(list).length;
  const monthSessions = workoutsThisMonth(list).length;
  const prs = useMemo(() => findPersonalRecords(list).slice(0, 12), [list]);

  async function removeWorkout(id: string, title: string) {
    if (!window.confirm(`Delete “${title}”?`)) return;
    await api(`/api/workouts/${id}`, { method: 'DELETE' });
    setMenuWorkoutId(null);
    refresh();
  }

  function formatWeight(kg: number) {
    const w = imperial ? Math.round(kgToLbs(kg) * 10) / 10 : Math.round(kg * 10) / 10;
    return `${w} ${imperial ? 'lb' : 'kg'}`;
  }

  return (
    <PremiumPage title="Workout history" onBack={() => nav('/workouts')} backLabel="Back to Train">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search workouts or exercises"
        className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
      />

      <div className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === f.id
                ? 'bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/30'
                : 'bg-white/[0.05] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex rounded-2xl bg-white/[0.04] p-1">
        {(['timeline', 'analytics', 'calendar'] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex-1 rounded-xl py-2 text-xs font-bold capitalize transition-colors ${
              tab === id ? 'bg-white/[0.1] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-muted)]'
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Loading history…</p>
      ) : tab === 'timeline' ? (
        filtered.length === 0 ? (
          <PremiumEmpty
            icon={<NavIcon name="workouts" className="h-5 w-5 text-sky-300" />}
            title="No workouts found"
            actionLabel="Back to Train"
            onAction={() => nav('/workouts')}
          />
        ) : (
          <ul className="space-y-2">
            {filtered.map((w) => (
              <li key={w.id}>
                <WorkoutTimelineCard
                  workout={w}
                  imperial={imperial}
                  menuOpen={menuWorkoutId === w.id}
                  onMenuToggle={() => setMenuWorkoutId((id) => (id === w.id ? null : w.id))}
                  onDelete={() => void removeWorkout(w.id, w.name)}
                />
              </li>
            ))}
          </ul>
        )
      ) : tab === 'analytics' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <PremiumCard className="!p-3">
              <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Streak</div>
              <div className="mt-1 font-display text-2xl font-bold text-amber-300">{streak > 0 ? `${streak}d` : '—'}</div>
            </PremiumCard>
            <PremiumCard className="!p-3">
              <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">All time</div>
              <div className="mt-1 font-display text-2xl font-bold">{list.length}</div>
              <div className="text-[10px] text-[var(--text-muted)]">sessions</div>
            </PremiumCard>
            <PremiumCard className="!p-3">
              <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Weekly volume</div>
              <div className="mt-1 font-display text-xl font-bold">{formatVolumeKg(weekVol, imperial)}</div>
              <div className="text-[10px] text-[var(--text-muted)]">{weekSessions} sessions</div>
            </PremiumCard>
            <PremiumCard className="!p-3">
              <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Monthly volume</div>
              <div className="mt-1 font-display text-xl font-bold">{formatVolumeKg(monthVol, imperial)}</div>
              <div className="text-[10px] text-[var(--text-muted)]">{monthSessions} sessions</div>
            </PremiumCard>
          </div>

          <PremiumCard className="!p-4">
            <SectionHeader title="Muscle balance" />
            <MuscleHeatmap workouts={list} imperial={imperial} />
          </PremiumCard>

          <PremiumCard className="!p-4">
            <SectionHeader title="Personal records" />
            {prs.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">Log weighted sets to track PRs.</p>
            ) : (
              <ul className="space-y-2">
                {prs.map((pr) => (
                  <li key={pr.exerciseName}>
                    <Link
                      to={`/workouts/history/${pr.workoutId}`}
                      className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04]"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-[var(--text-primary)]">{pr.exerciseName}</div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {new Date(pr.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-bold tabular-nums text-amber-300">
                          {formatWeight(pr.weightKg)} × {pr.reps}
                        </div>
                        <div className="text-[10px] text-[var(--text-muted)]">
                          e1RM {formatWeight(pr.estimated1RmKg)}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </PremiumCard>

          <PremiumCard className="!p-4">
            <SectionHeader title="Weekly sets" />
            <div className="flex h-24 items-end gap-1">
              {Array.from({ length: 7 }, (_, i) => {
                const key = addDaysToDateKey(localDateKey(), -6 + i);
                const dayWorkouts = list.filter((w) => localDateKeyFromIso(w.startedAt) === key);
                const sets = dayWorkouts.reduce((s, w) => s + workoutSetCount(w), 0);
                const max = 30;
                const h = sets > 0 ? Math.max(12, Math.round((sets / max) * 100)) : 4;
                const label = new Date(key + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'narrow' });
                return (
                  <div key={key} className="flex flex-1 flex-col items-center gap-1">
                    <div
                      className={`w-full rounded-t-md ${sets > 0 ? 'bg-sky-500/60' : 'bg-white/[0.06]'}`}
                      style={{ height: `${h}%` }}
                    />
                    <span className="text-[9px] text-[var(--text-muted)]">{label}</span>
                  </div>
                );
              })}
            </div>
          </PremiumCard>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMonthOffset((m) => m - 1)}
              className="rounded-lg px-2 py-1 text-sm text-[var(--text-muted)] hover:bg-white/[0.06]"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => setMonthOffset(0)}
              className="text-xs font-semibold text-accent"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setMonthOffset((m) => m + 1)}
              className="rounded-lg px-2 py-1 text-sm text-[var(--text-muted)] hover:bg-white/[0.06]"
              disabled={monthOffset >= 0}
            >
              ›
            </button>
          </div>
          <WorkoutMonthCalendar workouts={list} monthOffset={monthOffset} />
          <p className="text-center text-[11px] text-[var(--text-muted)]">Highlighted days have logged workouts</p>
        </div>
      )}
    </PremiumPage>
  );
}
